import React, { useEffect, useState } from 'react';
import { Tabs, Badge } from 'antd';
import { AuditOutlined, DatabaseOutlined, FileTextOutlined } from '@ant-design/icons';

import { useAccess } from '../../auth/AccessContext.jsx';
import ReportsSection from '../reports/ReportsSection';
import TransactionApproval from './approval/TransactionApproval';
import UnitRegistry from './registry/UnitRegistry';
import { useRunsBadge } from './unitPrices/useRunsBadge.js';
import { GOLD } from '../../config/palette.js';

/** Section tab comes from the second hash segment: `#transactions/registry`. No window (a server render): the first tab. */
const sectionFromHash = (keys) => {
    const hash = typeof window === 'undefined' ? '' : window.location.hash;
    let key = hash.replace('#', '').split('/')[1];
    // Unit Prices now lives on Fund Register. Old links open that tab.
    if (key === 'unitPrices') key = 'registry';
    return keys.includes(key) ? key : keys[0];
};

/**
 * Unit Registry module — section tabs across the top, one per transaction workspace. A tab shows
 * only when the Core granted its screen; the section keys are the screen keys /console/v1/me uses.
 *
 * Fund Register (which now includes unit prices), Transaction Approval and Reports read the Core
 * through the Console API (IP-112). The Fund Register badge counts the NAV runs waiting for this
 * person to approve (unitPrices/useRunsBadge.js), read again whenever a run is decided. Reports is
 * still the prototype's in-browser book.
 */
const TransactionsTab = () => {
    const { canView } = useAccess();
    const runsBadge = useRunsBadge();

    const allTabs = [
        {
            // Renamed from "Unit Registry": that is now the tab's own name. Unit Prices lives on this screen.
            key: 'registry',
            icon: <DatabaseOutlined />,
            label: (
                <span>
                    Fund Register{' '}
                    {runsBadge.text && <Badge count={runsBadge.text} color={GOLD.base} title={runsBadge.text + ' NAV runs waiting for you'} />}
                </span>
            )
        },
        {
            // The orders live in the Core now (IP-89): the tab counts them itself, on its own sub-tabs.
            key: 'approval',
            icon: <AuditOutlined />,
            label: 'Transaction Approval'
        },
        { key: 'reports', icon: <FileTextOutlined />, label: 'Reports' }
    ];
    // Prices-only readers still open Fund Register: that is where the runs and the price history are.
    const tabItems = allTabs.filter((t) => (t.key === 'registry' ? canView('registry') || canView('unitPrices') : canView(t.key)));
    const [section, setSection] = useState(() => sectionFromHash(tabItems.map((t) => t.key)));

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const [module, key] = window.location.hash.replace('#', '').split('/');
        if (module === 'transactions' && key === 'unitPrices') window.location.hash = 'transactions/registry';
    }, []);

    const SECTIONS = {
        approval: <TransactionApproval />,
        registry: <UnitRegistry onRunsChanged={runsBadge.reload} />,
        // No segment — the registry reports on the whole book, both desks.
        reports: <ReportsSection title="Unit Registry Statement" />
    };

    return (
        <div className="workspace">
            <Tabs
                className="workspace-tabs"
                activeKey={section}
                onChange={(key) => {
                    setSection(key);
                    window.location.hash = `transactions/${key}`;
                }}
                items={tabItems}
            />
            <div className="workspace-body">{SECTIONS[section]}</div>
        </div>
    );
};

export default TransactionsTab;
